/**
 * Command-phase action rules: cost, legality, and application.
 *
 * Split deliberately into three layers so the UI, the AI and the tests all agree:
 *
 *   `actionCost`  — what it costs right now, after discounts and surcharges
 *   `checkAction` — may this seat do this, right now? Failure carries a readable reason
 *                   that goes straight onto the HUD
 *   `applyAction` — mutate state. Assumes `checkAction` passed; re-checks anyway, because
 *                   a desync here would be invisible and unfixable
 *
 * Nothing in this file advances the phase. Turn passing and phase transitions belong to
 * the engine — actions only ever change the board.
 */

import {
  ACTIONS, FREE_JUMPS, HAZARD_CLAIM_ORE, MAX_FLEETS, PASS_FUEL_BONUS, RELICS,
  SYSTEM_CLASSES, TECH_MARKET_SIZE, ZERO_RESOURCES,
} from './constants';
import { eventJumpModifier, eventTradeRatio } from './events';
import { distance, parseKey } from './hex';
import {
  canAfford, effectiveHabitability, fleetCount, hasFleetAt, missingResource, playerOf,
  populationCap, surveyTargets, systemOf,
} from './queries';
import { TECHS, deployOreDiscount, ignoresHazardCost, moveFuelDiscount, prerequisiteMet, researchCost, trackLevel } from './tech';
import type {
  ActionKind, GameAction, GameState, HexKey, Legality, NoticeSink, PlayerId, Resources,
} from './types';

const OK: Legality = { ok: true };
const no = (reason: string): Legality => ({ ok: false, reason });

/* ------------------------------------------------------------------ movement */

/** Hexes a seat's fleet may travel in one Move action, after tech and the live event. */
export function effectiveJumpRange(state: GameState, seat: PlayerId): number {
  const player = playerOf(state, seat);
  const base =
    2 + trackLevel(player, 'drive') + (player.megastructure === 'gateNexus' ? 2 : 0);
  // Gravitic Shear docks everyone a hex, but never below one — a fleet that cannot move
  // at all makes the round unplayable for a boxed-in seat.
  return Math.max(1, base + eventJumpModifier(state));
}

/** Fuel a move of `jumps` hexes costs this seat. */
export function moveFuelCost(state: GameState, seat: PlayerId, jumps: number): number {
  const discount = moveFuelDiscount(playerOf(state, seat));
  if (discount === Infinity) return 0; // Gate Weaving
  return Math.max(0, jumps - FREE_JUMPS - discount);
}

/* ---------------------------------------------------------------------- cost */

/**
 * Resource cost of an action in the current state. Returns zeroes for actions whose
 * cost is not resource-shaped (or which are free).
 */
export function actionCost(state: GameState, action: GameAction): Resources {
  switch (action.kind) {
    case 'survey':
    case 'pass':
    case 'advancePhase':
    case 'submitBid':
      return { ...ZERO_RESOURCES };

    case 'move': {
      const jumps = action.path.length;
      return { ...ZERO_RESOURCES, fuel: moveFuelCost(state, action.player, jumps) };
    }

    case 'deployFleet': {
      const discount = deployOreDiscount(playerOf(state, action.player));
      const ore = discount === Infinity
        ? 0
        : Math.max(0, ACTIONS.deployFleet.cost.ore - discount);
      return { ...ACTIONS.deployFleet.cost, ore };
    }

    case 'claim': {
      const system = systemOf(state, action.systemId);
      const player = playerOf(state, action.player);
      const surcharge =
        system && !ignoresHazardCost(player) ? system.hazard * HAZARD_CLAIM_ORE : 0;
      return { ...ACTIONS.claim.cost, ore: ACTIONS.claim.cost.ore + surcharge };
    }

    case 'upgrade':
      return { ...ACTIONS.upgrade.cost };

    case 'terraform':
      return { ...ACTIONS.terraform.cost };

    case 'research':
      return {
        ...ZERO_RESOURCES,
        data: researchCost(playerOf(state, action.player), action.techId),
      };

    case 'trade': {
      const [give] = eventTradeRatio(state);
      const cost = { ...ZERO_RESOURCES };
      cost[action.give] = give;
      return cost;
    }

    case 'megastructure':
      return { ...ACTIONS.megastructure.cost };
  }
}

/** Command Points an action consumes. */
export function actionCp(action: GameAction): number {
  if (action.kind === 'advancePhase' || action.kind === 'submitBid') return 0;
  return ACTIONS[action.kind as ActionKind].cp;
}

/* ------------------------------------------------------------------ legality */

/** Shared preconditions for every command action: right phase, right seat, enough CP. */
function checkCommandPreconditions(state: GameState, seat: PlayerId, action: GameAction): Legality {
  if (state.phase !== 'command') return no('Not the Command phase.');

  const active = state.turnOrder[state.activeIndex];
  if (active !== seat) return no('Not your turn.');

  const player = playerOf(state, seat);
  if (player.passed) return no('You have already passed this round.');

  const cp = actionCp(action);
  if (player.cp < cp) return no(`Needs ${cp} Command Point${cp === 1 ? '' : 's'}.`);

  const cost = actionCost(state, action);
  if (!canAfford(player.resources, cost)) {
    return no(`Not enough ${missingResource(player.resources, cost)}.`);
  }
  return OK;
}

export function checkAction(state: GameState, action: GameAction): Legality {
  // Phase-machine and bid actions have their own rules; the engine owns those.
  if (action.kind === 'advancePhase') return OK;
  if (action.kind === 'submitBid') return checkBid(state, action.player, action.amount);

  const pre = checkCommandPreconditions(state, action.player, action);
  if (!pre.ok) return pre;

  const player = playerOf(state, action.player);

  switch (action.kind) {
    case 'pass':
      return OK;

    case 'survey': {
      const system = systemOf(state, action.systemId);
      if (!system) return no('No such system.');
      if (system.surveyed) return no('Already surveyed.');
      if (!surveyTargets(state, action.player).includes(action.systemId)) {
        return no('Must be next to one of your fleets or holdings.');
      }
      return OK;
    }

    case 'move': {
      const fleet = state.fleets[action.fleetId];
      if (!fleet) return no('No such fleet.');
      if (fleet.owner !== action.player) return no('Not your fleet.');
      if (action.path.length === 0) return no('Pick a destination.');

      const range = effectiveJumpRange(state, action.player);
      if (action.path.length > range) {
        return no(`Too far — range is ${range} jump${range === 1 ? '' : 's'}.`);
      }

      // Every step must exist and be adjacent to the previous one. Deep space is
      // traversable — it is empty, not impassable.
      let from = parseKey(fleet.at);
      const visited = new Set<HexKey>([fleet.at]);
      for (const step of action.path) {
        const target = systemOf(state, step);
        if (!target) return no('Path leaves the galaxy.');
        if (distance(from, target.coord) !== 1) return no('Path must be a chain of jumps.');
        if (visited.has(step)) return no('Path doubles back on itself.');
        visited.add(step);
        from = target.coord;
      }
      return OK;
    }

    case 'deployFleet': {
      const system = systemOf(state, action.systemId);
      if (!system) return no('No such system.');
      if (system.owner !== action.player) return no('Not your system.');
      if (system.development !== 'colony') return no('Fleets are built at Colonies.');
      if (fleetCount(state, action.player) >= MAX_FLEETS) {
        return no(`Fleet limit is ${MAX_FLEETS}.`);
      }
      return OK;
    }

    case 'claim': {
      const system = systemOf(state, action.systemId);
      if (!system) return no('No such system.');
      if (!system.surveyed) return no('Survey it first.');
      if (system.owner !== null) return no('Already claimed.');
      if (system.cls === 'deepSpace') return no('Nothing here to colonise.');
      if (system.habitability < 1) return no('Uninhabitable.');
      if (!hasFleetAt(state, action.systemId, action.player)) {
        return no('You need a fleet in the system.');
      }
      return OK;
    }

    case 'upgrade': {
      const system = systemOf(state, action.systemId);
      if (!system) return no('No such system.');
      if (system.owner !== action.player) return no('Not your system.');
      if (system.development !== 'outpost') return no('Only Outposts can be upgraded.');
      if (effectiveHabitability(state, system) < 2) {
        return no('Habitability 2 required — terraform it first.');
      }
      return OK;
    }

    case 'terraform': {
      const system = systemOf(state, action.systemId);
      if (!system) return no('No such system.');
      if (system.owner !== action.player) return no('Not your system.');
      if (system.habitability >= 3 && system.hazard === 0) {
        return no('Already fully terraformed.');
      }
      return OK;
    }

    case 'research': {
      if (!state.techMarket.includes(action.techId)) return no('Not in the tech market.');
      if (player.techs.includes(action.techId)) return no('Already researched.');
      if (!prerequisiteMet(player, action.techId)) {
        const t = TECHS[action.techId];
        return no(`Needs ${t.track} tier ${t.tier - 1} first.`);
      }
      return OK;
    }

    case 'trade': {
      if (action.give === action.take) return no('Pick two different resources.');
      return OK;
    }

    case 'megastructure': {
      if (player.megastructure !== null) return no('You have already built one.');
      const system = systemOf(state, action.systemId);
      if (!system) return no('No such system.');
      if (system.owner !== action.player) return no('Not your system.');
      if (system.development !== 'colony') return no('Requires a Colony.');
      if (system.megastructure !== null) return no('This system already has one.');
      return OK;
    }
  }
}

/** Bid legality, shared by contests and auctions. */
export function checkBid(state: GameState, seat: PlayerId, amount: number): Legality {
  const bid = state.activeBid;
  if (!bid) return no('No bid in progress.');
  if (!bid.participants.includes(seat)) return no('You are not in this bid.');
  if (bid.bids[seat] !== undefined) return no('Already locked in.');
  if (!Number.isInteger(amount) || amount < 0) return no('Bid must be a whole number.');

  const player = playerOf(state, seat);
  if (player.resources[bid.resource] < amount) {
    return no(`You only have ${player.resources[bid.resource]} ${bid.resource}.`);
  }
  return OK;
}

/* --------------------------------------------------------------- application */

/**
 * Apply a legal command action. Mutates `state`.
 *
 * Re-validates first and throws on an illegal action: reaching here with something
 * illegal means a caller skipped `checkAction`, which is a programming error, not a
 * player error. Player errors are caught by the UI before they get this far.
 */
export function applyCommandAction(
  state: GameState,
  action: GameAction,
  notice?: NoticeSink,
): void {
  const legality = checkAction(state, action);
  if (!legality.ok) {
    throw new Error(`applyCommandAction: illegal ${action.kind} — ${legality.reason}`);
  }
  if (action.kind === 'advancePhase' || action.kind === 'submitBid') {
    throw new Error(`applyCommandAction: ${action.kind} is the engine's job`);
  }

  const player = playerOf(state, action.player);
  const cost = actionCost(state, action);

  // Charge CP and resources up front so every branch below can assume payment happened.
  player.cp -= actionCp(action);
  player.resources.ore -= cost.ore;
  player.resources.fuel -= cost.fuel;
  player.resources.biomass -= cost.biomass;
  player.resources.data -= cost.data;
  player.stats.actionsTaken++;

  switch (action.kind) {
    case 'pass': {
      player.passed = true;
      player.resources.fuel += PASS_FUEL_BONUS;
      log(state, action.player, 'passes, banking 1 Fuel.');
      break;
    }

    case 'survey': {
      revealSystem(state, action.player, action.systemId);
      player.resources.data += 1;

      // Deep Survey reveals a second system. Pick the lowest key among the freshly
      // available targets so replays are identical.
      if (player.techs.includes('scienceI')) {
        const extra = surveyTargets(state, action.player)[0];
        if (extra) revealSystem(state, action.player, extra);
      }
      break;
    }

    case 'move': {
      const fleet = state.fleets[action.fleetId]!;
      const destination = action.path[action.path.length - 1]!;
      fleet.at = destination;
      fleet.movedThisRound += action.path.length;
      log(state, action.player, `jumps a fleet to ${destination}.`);
      break;
    }

    case 'deployFleet': {
      const id = `f${state.nextFleetSeq++}`;
      state.fleets[id] = { id, owner: action.player, at: action.systemId, movedThisRound: 0 };
      log(state, action.player, `commissions a fleet at ${action.systemId}.`);
      break;
    }

    case 'claim': {
      const system = systemOf(state, action.systemId)!;
      system.owner = action.player;
      system.development = 'outpost';
      player.stats.outpostsBuilt++;
      takeRelic(state, action.player, action.systemId, notice);
      log(state, action.player, `founds an Outpost on ${SYSTEM_CLASSES[system.cls].name} ${system.id}.`);
      notice?.({
        type: 'claimed', player: action.player, systemId: action.systemId, development: 'outpost',
      });
      break;
    }

    case 'upgrade': {
      const system = systemOf(state, action.systemId)!;
      system.development = 'colony';
      player.population = Math.min(populationCap(player), player.population + 1);
      player.stats.coloniesBuilt++;
      log(state, action.player, `raises a Colony on ${system.id}.`);
      notice?.({
        type: 'claimed', player: action.player, systemId: action.systemId, development: 'colony',
      });
      break;
    }

    case 'terraform': {
      const system = systemOf(state, action.systemId)!;
      system.habitability = Math.min(3, system.habitability + 1);
      system.hazard = Math.max(0, system.hazard - 1);
      player.stats.terraformed++;
      log(state, action.player, `terraforms ${system.id} to habitability ${system.habitability}.`);
      break;
    }

    case 'research': {
      player.techs.push(action.techId);
      state.techMarket = state.techMarket.filter((id) => id !== action.techId);
      refillTechMarket(state);
      log(state, action.player, `researches ${TECHS[action.techId].name}.`);
      notice?.({ type: 'tech', player: action.player, techId: action.techId });
      break;
    }

    case 'trade': {
      const [, take] = eventTradeRatio(state);
      player.resources[action.take] += take;
      log(state, action.player, `trades ${action.give} for ${action.take}.`);
      break;
    }

    case 'megastructure': {
      const system = systemOf(state, action.systemId)!;
      system.megastructure = action.pick;
      player.megastructure = action.pick;
      log(state, action.player, `completes a ${action.pick} at ${system.id}.`);
      notice?.({ type: 'megastructure', player: action.player, kind: action.pick });
      break;
    }
  }

  if (action.kind !== 'pass') {
    notice?.({
      type: 'action',
      kind: action.kind,
      player: action.player,
      systemId: 'systemId' in action ? action.systemId : undefined,
    });
  }

  // Spending your last Command Point takes you out of the round automatically —
  // otherwise the round-robin would keep handing the baton to someone with nothing to do.
  if (player.cp <= 0) player.passed = true;
}

/* ------------------------------------------------------------------- helpers */

function revealSystem(state: GameState, seat: PlayerId, systemId: HexKey): void {
  const system = systemOf(state, systemId);
  if (!system || system.surveyed) return;
  system.surveyed = true;
  playerOf(state, seat).stats.systemsSurveyed++;
  log(state, seat, `charts ${SYSTEM_CLASSES[system.cls].name} ${systemId}.`);
}

/** Claiming a relic system pays out once, to the first claimant. */
function takeRelic(
  state: GameState,
  seat: PlayerId,
  systemId: HexKey,
  notice?: NoticeSink,
): void {
  const system = systemOf(state, systemId);
  if (!system || !system.relic || system.relicTaken) return;

  const relic = RELICS[system.relic];
  const player = playerOf(state, seat);
  player.resources.ore += relic.payout.ore;
  player.resources.fuel += relic.payout.fuel;
  player.resources.biomass += relic.payout.biomass;
  player.resources.data += relic.payout.data;
  player.relics.push(system.relic);
  system.relicTaken = true;

  log(state, seat, `recovers the ${relic.name}.`);
  notice?.({ type: 'relic', player: seat, relic: system.relic });
}

/** Top the tech market back up to size, in deck order. */
export function refillTechMarket(state: GameState): void {
  while (state.techMarket.length < TECH_MARKET_SIZE && state.techDeck.length > 0) {
    state.techMarket.push(state.techDeck.shift()!);
  }
}

export function log(state: GameState, player: PlayerId | null, text: string): void {
  state.log.push({ round: state.round, phase: state.phase, player, text });
  // The log is a ring buffer — a full match generates a few hundred lines and the
  // scoreboard only ever shows the tail.
  if (state.log.length > 600) state.log.splice(0, state.log.length - 600);
}

/* ------------------------------------------------------------- UI affordances */

/**
 * Which action kinds this seat could legally start right now.
 *
 * Used to grey out the radial menu and to bound the AI's search. Each kind is probed
 * with a representative target, so this answers "is there any legal `claim`?" rather
 * than "is this specific claim legal".
 */
export function legalActionKinds(state: GameState, seat: PlayerId): ActionKind[] {
  const out: ActionKind[] = [];
  for (const kind of Object.keys(ACTIONS) as ActionKind[]) {
    if (someActionOfKindIsLegal(state, seat, kind)) out.push(kind);
  }
  return out;
}

function someActionOfKindIsLegal(state: GameState, seat: PlayerId, kind: ActionKind): boolean {
  for (const candidate of enumerateActions(state, seat, kind)) {
    if (checkAction(state, candidate).ok) return true;
  }
  return false;
}

/**
 * Every concrete action of a given kind this seat might take. The AI enumerates and
 * scores these; the UI uses them to build target highlight sets.
 *
 * Deliberately bounded: `move` only enumerates shortest paths to reachable systems, not
 * every permutation of jumps, which keeps the branching factor sane.
 */
export function enumerateActions(
  state: GameState,
  seat: PlayerId,
  only?: ActionKind,
): GameAction[] {
  const out: GameAction[] = [];
  const want = (kind: ActionKind) => only === undefined || only === kind;
  const player = playerOf(state, seat);

  if (want('pass')) out.push({ kind: 'pass', player: seat });

  if (want('survey')) {
    for (const systemId of surveyTargets(state, seat)) {
      out.push({ kind: 'survey', player: seat, systemId });
    }
  }

  if (want('move')) {
    const range = effectiveJumpRange(state, seat);
    for (const fleet of Object.values(state.fleets)) {
      if (fleet.owner !== seat) continue;
      const from = parseKey(fleet.at);
      for (const system of Object.values(state.systems)) {
        const d = distance(from, system.coord);
        if (d === 0 || d > range) continue;
        const path = straightIshPath(state, fleet.at, system.id, range);
        if (path) out.push({ kind: 'move', player: seat, fleetId: fleet.id, path });
      }
    }
  }

  if (want('deployFleet')) {
    for (const system of Object.values(state.systems)) {
      if (system.owner === seat && system.development === 'colony') {
        out.push({ kind: 'deployFleet', player: seat, systemId: system.id });
      }
    }
  }

  if (want('claim')) {
    for (const fleet of Object.values(state.fleets)) {
      if (fleet.owner !== seat) continue;
      out.push({ kind: 'claim', player: seat, systemId: fleet.at });
    }
  }

  if (want('upgrade') || want('terraform') || want('megastructure')) {
    for (const system of Object.values(state.systems)) {
      if (system.owner !== seat) continue;
      if (want('upgrade')) out.push({ kind: 'upgrade', player: seat, systemId: system.id });
      if (want('terraform')) out.push({ kind: 'terraform', player: seat, systemId: system.id });
      if (want('megastructure') && player.megastructure === null) {
        for (const pick of ['ringworld', 'dysonSwarm', 'gateNexus', 'arkFoundry'] as const) {
          out.push({ kind: 'megastructure', player: seat, systemId: system.id, pick });
        }
      }
    }
  }

  if (want('research')) {
    for (const techId of state.techMarket) {
      out.push({ kind: 'research', player: seat, techId });
    }
  }

  if (want('trade')) {
    const kinds = ['ore', 'fuel', 'biomass', 'data'] as const;
    for (const give of kinds) {
      for (const take of kinds) {
        if (give !== take) out.push({ kind: 'trade', player: seat, give, take });
      }
    }
  }

  return out;
}

/**
 * A concrete jump chain from `from` to `to` within `range`.
 *
 * Walks the hex line and repairs any step that is not a legal neighbour (cube rounding
 * on a line can produce a diagonal), falling back to a BFS if the line is unusable.
 * Returns `null` when there is no chain inside range.
 */
export function straightIshPath(
  state: GameState,
  from: HexKey,
  to: HexKey,
  range: number,
): HexKey[] | null {
  const start = parseKey(from);
  const goal = parseKey(to);
  const d = distance(start, goal);
  if (d === 0 || d > range) return null;

  // Greedy walk: each step, move to whichever existing neighbour gets closest to the
  // goal and has not been used. On a full hex map this always succeeds in exactly `d`
  // steps, which is also the cheapest possible route.
  const path: HexKey[] = [];
  const used = new Set<HexKey>([from]);
  let cursor = start;

  for (let step = 0; step < range; step++) {
    let best: { key: HexKey; coord: { q: number; r: number }; dist: number } | null = null;
    for (const dir of [
      { q: 1, r: 0 }, { q: 1, r: -1 }, { q: 0, r: -1 },
      { q: -1, r: 0 }, { q: -1, r: 1 }, { q: 0, r: 1 },
    ]) {
      const next = { q: cursor.q + dir.q, r: cursor.r + dir.r };
      const nk = `${next.q},${next.r}`;
      if (used.has(nk) || !state.systems[nk]) continue;
      const nd = distance(next, goal);
      if (best === null || nd < best.dist) best = { key: nk, coord: next, dist: nd };
    }
    if (!best) return null;

    path.push(best.key);
    used.add(best.key);
    cursor = best.coord;
    if (best.dist === 0) return path;
  }
  return null;
}
